/**
 * TEXT FORMAT (MODES.md §3c) — the composer's Bold / Italic / Normal / bullets / numbers, WITHOUT a rich-text library:
 * each action edits the plain textarea's selection by inserting MARKDOWN (``**bold**``, ``_italic_``, ``- ``, ``1. ``).
 * The message is stored exactly as typed (Markdown text), so the formatting is preserved everywhere it's shown —
 * the stream renders it with the shared MarkdownRenderer. Pure: (value, start, end) → {value, start, end}.
 */

const MARKS = { bold: '**', italic: '_' };

function wrapped(value, start, end, m) {
  // markers just OUTSIDE the selection ("**|text|**") or INSIDE its edges ("|**text**|")
  if (value.slice(start - m.length, start) === m && value.slice(end, end + m.length) === m) return 'outside';
  const sel = value.slice(start, end);
  if (sel.length >= 2 * m.length && sel.startsWith(m) && sel.endsWith(m)) return 'inside';
  return '';
}

/** Bold / Italic: wrap the selection; already wrapped → unwrap; nothing selected → a marker pair, caret between. */
export function toggleMark(value = '', start = 0, end = start, kind = 'bold') {
  const m = MARKS[kind] || MARKS.bold;
  let s = Math.min(start, end); let e = Math.max(start, end);
  // italic `_` must not mistake a bold `**` edge for its own (and vice versa) — trim whitespace off the selection
  while (s < e && /\s/.test(value[s])) s += 1;
  while (e > s && /\s/.test(value[e - 1])) e -= 1;
  const w = wrapped(value, s, e, m);
  if (w === 'outside') {
    return { value: value.slice(0, s - m.length) + value.slice(s, e) + value.slice(e + m.length), start: s - m.length, end: e - m.length };
  }
  if (w === 'inside') {
    const inner = value.slice(s + m.length, e - m.length);
    return { value: value.slice(0, s) + inner + value.slice(e), start: s, end: s + inner.length };
  }
  return { value: value.slice(0, s) + m + value.slice(s, e) + m + value.slice(e), start: s + m.length, end: e + m.length };
}

/** The whole lines a selection touches → [lineStart, lineEnd]. */
function lineSpan(value, start, end) {
  const a = value.lastIndexOf('\n', Math.max(0, Math.min(start, end) - 1)) + 1;
  const nl = value.indexOf('\n', Math.max(start, end));
  return [Math.min(start, end) === 0 ? 0 : a, nl === -1 ? value.length : nl];
}

const BULLET = /^(\s*)[-*•]\s+/;
const NUMBER = /^(\s*)\d+[.)]\s+/;

/** Bullets / numbers on every selected line; all lines already that kind → remove it; the other kind → switch. */
export function toggleList(value = '', start = 0, end = start, kind = 'bullet') {
  const [a, b] = lineSpan(value, start, end);
  const lines = value.slice(a, b).split('\n');
  const is = kind === 'number' ? NUMBER : BULLET;
  const all = lines.filter((l) => l.trim()).every((l) => is.test(l)) && lines.some((l) => l.trim());
  let n = 0;
  const next = lines.map((l) => {
    const bare = l.replace(BULLET, '$1').replace(NUMBER, '$1');
    if (all) return bare;
    if (!l.trim() && lines.length > 1) return l;
    n += 1;
    const indent = (bare.match(/^\s*/) || [''])[0];
    return `${indent}${kind === 'number' ? `${n}. ` : '- '}${bare.slice(indent.length)}`;
  }).join('\n');
  const out = value.slice(0, a) + next + value.slice(b);
  // one empty line just made a list → the caret goes after the marker
  if (start === end && !value.slice(a, b).trim()) return { value: out, start: a + next.length, end: a + next.length };
  return { value: out, start: a, end: a + next.length };
}

/** Normal: strip bold / italic marks and list markers from the selected lines (back to plain text). */
export function clearFormat(value = '', start = 0, end = start) {
  const [a, b] = lineSpan(value, start, end);
  const plain = value.slice(a, b).split('\n')
    .map((l) => l.replace(BULLET, '$1').replace(NUMBER, '$1')
      .replace(/\*\*(.+?)\*\*/g, '$1').replace(/__(.+?)__/g, '$1')
      .replace(/(^|[^\w*])\*(?!\s)(.+?)\*(?!\w)/g, '$1$2').replace(/(^|[^\w])_(?!\s)(.+?)_(?!\w)/g, '$1$2'))
    .join('\n');
  return { value: value.slice(0, a) + plain + value.slice(b), start: a, end: a + plain.length };
}

/** Shift+Enter inside a list item → the next item ("- " / the next number); on an EMPTY item → end the list.
 *  → null when the caret isn't on a list line (a plain newline then). */
export function continueList(value = '', caret = 0) {
  const a = value.lastIndexOf('\n', caret - 1) + 1;
  const line = value.slice(a, caret);
  const num = line.match(/^(\s*)(\d+)[.)]\s+/);
  const bul = line.match(/^(\s*)([-*•])\s+/);
  const m = num || bul;
  if (!m) return null;
  if (!line.slice(m[0].length).trim()) {                       // an empty item → leave the list
    const v = value.slice(0, a) + value.slice(caret);
    return { value: v, start: a, end: a };
  }
  const marker = num ? `${m[1]}${Number(num[2]) + 1}. ` : `${m[1]}- `;
  const ins = `\n${marker}`;
  return { value: value.slice(0, caret) + ins + value.slice(caret), start: caret + ins.length, end: caret + ins.length };
}

/** The keyboard shortcuts (Slack's): ⌘/Ctrl+B bold, +I italic, +Shift+7 numbers, +Shift+8 bullets. → action | ''. */
export function formatShortcut(e) {
  if (!e || !(e.metaKey || e.ctrlKey) || e.altKey) return '';
  const k = String(e.key || '').toLowerCase();
  if (!e.shiftKey && k === 'b') return 'bold';
  if (!e.shiftKey && k === 'i') return 'italic';
  if (e.shiftKey && (e.code === 'Digit7' || k === '7' || k === '&')) return 'number';
  if (e.shiftKey && (e.code === 'Digit8' || k === '8' || k === '*')) return 'bullet';
  return '';
}

/** One entry point for the toolbar + shortcuts. */
export function applyFormat(action, value, start, end) {
  if (action === 'bold' || action === 'italic') return toggleMark(value, start, end, action);
  if (action === 'bullet' || action === 'number') return toggleList(value, start, end, action);
  if (action === 'normal') return clearFormat(value, start, end);
  return { value, start, end };
}

/** A human message as Markdown that KEEPS its line breaks (one Enter = one line, as typed) — outside code fences a
 *  single newline becomes a Markdown hard break. */
export function keepLineBreaks(text = '') {
  return String(text || '').split(/(```[\s\S]*?(?:```|$))/g)
    .map((part, i) => (i % 2 ? part : part.replace(/([^\n])\n(?=[^\n])/g, '$1  \n')))
    .join('');
}
